import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";
import { APIS } from "src/dm/utils/constants";

export interface DealAPVendor {
  dealId: number;
  apVendorNumber: string;
}

interface Envelope<T> {
  items: T[];
  total: number;
}

const url = (dealId: number) => `${APIS.Deals}/${dealId}/ap-vendors`;

/**
 * Audit item #6 — DEAL_AP_VENDOR child rows. Legacy NewDeal save semantics
 * are delete-then-insert; the API surfaces a PUT that replaces the entire
 * set in a single transaction.
 */
export const useDealAPVendors = (dealId: number | undefined) =>
  useQuery({
    queryKey: ["deal-ap-vendors", dealId],
    enabled: dealId !== undefined && dealId > 0,
    queryFn: async (): Promise<DealAPVendor[]> => {
      const response = await apiClient.get<Envelope<DealAPVendor>>(
        url(dealId as number)
      );
      return response.data.items;
    },
  });

export const useReplaceDealAPVendors = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (apVendorNumbers: string[]): Promise<DealAPVendor[]> => {
      const response = await apiClient.put<Envelope<DealAPVendor>>(url(dealId), {
        apVendorNumbers,
      });
      return response.data.items;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["deal-ap-vendors", dealId] }),
  });
};

export const useDeleteDealAPVendor = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (apVendorNumber: string): Promise<void> => {
      await apiClient.delete(
        `${url(dealId)}/${encodeURIComponent(apVendorNumber)}`
      );
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["deal-ap-vendors", dealId] }),
  });
};
